'use client';

import { createClient } from '@/lib/supabase/client';

export type Testimonial = {
  id: string;
  clientName: string;
  companyName: string;
  companyType: string;
  location: string;
  projectTitle: string;
  projectTitleZh: string;
  content: string;
  contentZh: string;
  rating: number;
  productCategories: string[];
  orderVolume: string;
  avatarInitials: string;
  avatarColor: string;
  isFeatured: boolean;
  createdAt: string;
};

export type ReviewSubmission = {
  clientName: string;
  companyName: string;
  companyType: string;
  location: string;
  email: string;
  rating: number;
  content: string;
  productCategories: string[];
};

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function mapRow(row: any): Testimonial {
  return {
    id: row.id,
    clientName: row.client_name,
    companyName: row.company_name,
    companyType: row.company_type,
    location: row.location,
    projectTitle: row.project_title,
    projectTitleZh: row.project_title_zh,
    content: row.content,
    contentZh: row.content_zh,
    rating: row.rating,
    productCategories: row.product_categories || [],
    orderVolume: row.order_volume,
    avatarInitials: row.avatar_initials,
    avatarColor: row.avatar_color,
    isFeatured: row.is_featured,
    createdAt: row.created_at,
  };
}

export const testimonialService = {
  async getAll(): Promise<Testimonial[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching testimonials:', error.message);
          throw error;
        }
        console.log('Data error fetching testimonials:', error.message);
        return [];
      }
      return data?.map(mapRow) || [];
    } catch (error: any) {
      console.log('Testimonial fetch error:', error.message);
      throw error;
    }
  },

  async getFeatured(): Promise<Testimonial[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_active', true)
        .eq('is_featured', true)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching featured testimonials:', error.message);
          throw error;
        }
        console.log('Data error fetching featured testimonials:', error.message);
        return [];
      }
      return data?.map(mapRow) || [];
    } catch (error: any) {
      console.log('Featured testimonial fetch error:', error.message);
      throw error;
    }
  },

  async submitReview(submission: ReviewSubmission): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const initials = submission.clientName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
      const colors = ['bg-orange', 'bg-navy', 'bg-lightblue', 'bg-green-600'];
      const avatarColor = colors[Math.floor(Math.random() * colors.length)];

      const { error } = await supabase.from('testimonials').insert({
        client_name: submission.clientName,
        company_name: submission.companyName,
        company_type: submission.companyType,
        location: submission.location,
        submitted_by_email: submission.email,
        project_title: 'Customer Review',
        project_title_zh: '客戶評價',
        content: submission.content,
        content_zh: submission.content,
        rating: submission.rating,
        product_categories: submission.productCategories,
        order_volume: '',
        avatar_initials: initials,
        avatar_color: avatarColor,
        is_featured: false,
        is_active: false,
        is_pending_review: true,
      });

      if (error) {
        console.error('Review submission error:', error.message);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err: any) {
      console.error('Review submission exception:', err.message);
      return { success: false, error: err.message };
    }
  },
};
